import { BANKS } from '../../data/banks'
import { useSection } from '../../cms/cms-context'
import { useGsapStagger } from '../../hooks/useGsapReveal'
import Button from '../ui/Button'
import Section from '../ui/Section'
import { CmsHeading } from '../../cms/CmsHeading'

/* Wordmarks in one weight and one colour. Bank logos in their own brand
   colours would pull the eye straight off our own palette, and we do not
   hold licences for the artwork anyway. */
/* The partner list this section shipped with, in the shape the CMS reads.
   Derived from data/banks rather than typed out again — that file is still
   what the lease calculator works from, so a bank added there is a bank this
   section falls back to. Once the console has saved its own list, that is what
   renders, and the two part company: which is the point.

   A card carries the name and nothing else. Every rate, product name and
   Islamic-or-conventional label a bank publishes moves faster than this site
   does, and a stale one on a wall of eight is worse than none at all: the
   number the visitor actually needs comes out of the calculator, against the
   car they are looking at. */
const BANK_ITEMS = BANKS.map((bank) => ({
  id: bank.id,
  content: { name: bank.name },
}))

function BankPartners() {
  const cms = useSection()
  const banks = cms.items(BANK_ITEMS)

  const ref = useGsapStagger<HTMLUListElement>('[data-bank]', {
    y: 18,
    stagger: 0.05,
  })

  return (
    <Section tone="ink" labelledBy="banks-title">
      <CmsHeading
        id="banks-title"
        eyebrow="Our financing partners"
        title="Multiple financing options"
        lede="Eight partner banks, Islamic and conventional. We put one file in front of all of them and bring back every number."
        action={
          <Button
            variant="ghost"
            to={cms.text('ctaHref', '/financing')}
            arrow
            {...cms.field('cta')}
          >
            {cms.text('cta', 'How financing works')}
          </Button>
        }
      />

      <ul
        ref={ref}
        className="mt-12 grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4"
      >
        {banks.map((bank) => (
          <li
            key={bank.id}
            data-bank
            {...bank.props}
            className="group flex min-h-32 items-center justify-center bg-raised p-7 text-center transition-colors duration-300 hover:bg-surface"
          >
            <span className="font-display text-[19px] font-semibold tracking-[-0.02em] text-steel transition-colors duration-300 group-hover:text-platinum">
              {bank.content.name}
            </span>
          </li>
        ))}
      </ul>

      <p {...cms.field('footnote')} className="mt-6 text-[13px] text-dim">
        {cms.text(
          'footnote',
          'We are tied to none of them. The rate and the terms come from the bank at approval, and the one that comes back best is the one you sign with.',
        )}
      </p>
    </Section>
  )
}

export default BankPartners
